﻿<script>
	import { registerPageComplete } from "../../tealium";
    import model from "../../stores/modelStore";
	import textStore from "../../stores/textStore";
    import { projectSettings, acceptedFileUploadList } from "../../config";
    import { isValid } from "../../validation";

    import PageSection from "../../components/PageSection.svelte";
    import Form from "../../components/Form.svelte";
    import FormRow from "../../components/FormRow.svelte";
    import FormColumn from "../../components/FormColumn.svelte";
    import YesNoGroup from "../../components/YesNoGroup.svelte";
    import TextInput from "../../components/TextInput.svelte";
    import TextArea from "../../components/TextArea.svelte";
    import RadioGroup from "../../components/RadioGroup.svelte";
    import RadioGroupWithImage from "../../components/RadioGroupWithImage.svelte";
    import FileUpload from "../../components/FileUpload.svelte";
    import MonthYearSelect from "../../components/MonthYearSelect.svelte";
    import ClassActionEmailAddress from "../../components/ClassActionEmailAddress.svelte";
    import ClassActionPhoneNumber from "../../components/ClassActionPhoneNumber.svelte";
    import NavigationButtons from "../../components/NavigationButtons.svelte";
    import EmploymentPlaces from "../../components/EmploymentPlaces.svelte";
    import Sibling from "../../components/Sibling.svelte";

    export let disabled = null;
    export let pageName = null;
	export let pageTitle = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;

    const workDocumentFilePrefix = "WORK";
	let required = false;
	let buttons = null;

    if (!disabled) {
        $model.lastPageVisited = lastVisitedPage;
        document.title = pageTitle;
    }

    const handleSubmit = async () => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {

            // clean up
            cleanSelections();

			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(response);
			} else {
				registerPageComplete(
					pageName,
					$model.contact.emailAddress,
					$model.contact.phoneNumber,
				);
				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	const cleanSelections = () => {
        if ($model.claimDetail && $model.claimDetail.employmentDetails){
            const filteredArray = $model.claimDetail.employmentDetails.filter(obj => 
                obj.placeOfEmployment || obj.years || obj.typeOfWork || obj.locationOfEmployment
            );
            $model.claimDetail.employmentDetails = filteredArray;
        }

        if ($model.claimDetail && $model.claimDetail.siblingDetails){
            const filteredArray = $model.claimDetail.siblingDetails.filter(obj => obj.siblingsName);
            $model.claimDetail.siblingDetails = filteredArray;
        }
	};


    //$:{
    //    console.log("Living Claimants", $model);
    //}
</script>

<PageSection>
	<Form id="form-livingClaimants-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Living Claimants</h1>
			</FormColumn>
		</FormRow>
        	
        <FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<p>Only complete this section if you are registering your own claim (an Aboriginal or Torres Strait Islander person who worked in Western Australia between 11 December 1936 and 9 June 1972)</p>
			</FormColumn>
		</FormRow>

		<div class="confirmation-box-shadow">

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.workedDuringPeriod}
                        name="workedDuringPeriod"
                        label="Did you work in Western Australia between 11 December 1936 and 9 June 1972?"
                    />
                </FormColumn>
            </FormRow>

            <EmploymentPlaces
                required={false}
			    {disabled}
                bind:employmentDetails={$model.claimDetail.employmentDetails}
                label="Please provide details below of AT LEAST ONE PLACE you worked between 11 December 1936 and 9 June 1972 for no money or only a small amount of money. For example, on a station, or at a mission that you can name below."
                />

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.paidAnyMoney}
                        name="paidAnyMoney"
                        label="Were you paid no money or only a small amount of money for all or some of this work?"
                    />
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.haveWorkDocuments}
                        name="haveWorkDocuments"
                        label="Do you have any documents about your work between 11 December 1936 and 9 June 1972?"
                    />
                </FormColumn>
            </FormRow>

            {#if $model.claimDetail.haveWorkDocuments === "Yes"}
            <FormRow>
                <FormColumn>
                    {#if (!disabled)}
                        <p>
                            Please upload copies of these documents. 
                            This is not compulsory but any documents that are available may assist with registering and paying the claimant compensation money.
                        </p>
                    {/if}
                    <FileUpload
                        {disabled}
                        registrationToken={$model.registrationToken}
                        filenamePrefix="{workDocumentFilePrefix}"
					    label="Please upload copies of these documents. This is not compulsory but any documents that are available may assist with registering and paying the claimant compensation money."
                        bind:documentList={$model.workDocumentList}>
                    </FileUpload>
                </FormColumn>
            </FormRow>
            {/if}

            <FormRow>
                <FormColumn>
                    <p>The WA government may have records about your work history that could assist your claim.</p>
                </FormColumn>
            </FormRow>

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.consentShineProvideInfoToGov}
                        name="consentShineProvideInfoToGov"
                        label="Do you consent to Shine Lawyers providing the information in the Registration Form (including your personal details) to the WA Government to request records about the claimant?"
                    />
                    <p>This is not compulsory but if the State can provide information it may assist with registering and showing the eligibility of the claimant.</p>
                    <p>Even if you tick "No", the State may be given some information about you during the administration process, including your name and date of birth.</p>
                </FormColumn>
            </FormRow>

            <Sibling
                required={false}
			    {disabled}
                bind:siblingDetails={$model.claimDetail.siblingDetails}
                bind:claimDetail={$model.claimDetail}
                label="If you want the WA Government to look for your records, providing all or some of the information below about your family might make that easier.  This is not compulsory."
                />
		</div>

        
        <FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons
					nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"} />
			</FormColumn>
		</FormRow>
    </Form>
</PageSection>

<style>
    h1 {
        margin-bottom: 20px;
    }
</style>